/**
 * React hooks for subscribing to LCVFR artifacts.
 *
 * These hooks provide the UI with reactive access to the latest
 * artifact for a given type. The store uses sequence-based ordering
 * to ensure the UI always renders the freshest data.
 *
 * @module hooks/useLcvfrArtifact
 */

import { useMemo } from 'react';
import { useLcvfrStore, type LcvfrArtifactEntry } from '@/lib/stores/lcvfr-store';
import type { ArtifactType, EncounterId } from '@assistmd/types';

/**
 * Subscribe to the latest artifact of a given type.
 *
 * Returns null if no artifact of that type exists for the active encounter.
 * Automatically re-renders when a higher-sequence artifact arrives.
 */
export function useLcvfrArtifact(type: ArtifactType): LcvfrArtifactEntry | null {
  return useLcvfrStore((s) => s.latestArtifacts[type] ?? null);
}

/**
 * Subscribe to the latest SOAP artifact (soap.live or soap.final).
 *
 * Returns the parsed SOAP sections from contentJson, or null.
 * Prefers soap.final over soap.live if both exist.
 */
export function useLcvfrSoap(): {
  soap: { subjective: string; objective: string; assessment: string; plan: string } | null;
  source: 'live' | 'final' | null;
  sequence: number;
  runId: string | null;
} {
  const finalArtifact = useLcvfrStore((s) => s.latestArtifacts['soap.final'] ?? null);
  const liveArtifact = useLcvfrStore((s) => s.latestArtifacts['soap.live'] ?? null);

  return useMemo(() => {
    // Prefer final over live
    const artifact = finalArtifact ?? liveArtifact;
    if (!artifact) {
      return { soap: null, source: null, sequence: 0, runId: null };
    }

    const source: 'live' | 'final' = finalArtifact ? 'final' : 'live';

    // Extract SOAP sections from contentJson or contentText
    let soap: { subjective: string; objective: string; assessment: string; plan: string } | null = null;

    if (artifact.contentJson) {
      const json = artifact.contentJson;
      soap = {
        subjective: (json.subjective as string) ?? '',
        objective: (json.objective as string) ?? '',
        assessment: (json.assessment as string) ?? '',
        plan: (json.plan as string) ?? '',
      };
    } else if (artifact.contentText) {
      // Fallback: try to parse contentText as JSON
      try {
        const parsed = JSON.parse(artifact.contentText) as Record<string, unknown>;
        soap = {
          subjective: (parsed.subjective as string) ?? '',
          objective: (parsed.objective as string) ?? '',
          assessment: (parsed.assessment as string) ?? '',
          plan: (parsed.plan as string) ?? '',
        };
      } catch {
        // Raw text — put it all in subjective
        soap = {
          subjective: artifact.contentText,
          objective: '',
          assessment: '',
          plan: '',
        };
      }
    }

    return {
      soap,
      source,
      sequence: artifact.sequence,
      runId: artifact.runId as string,
    };
  }, [finalArtifact, liveArtifact]);
}

/**
 * Whether the LCVFR pipeline is currently processing.
 */
export function useLcvfrPipelineActive(): boolean {
  return useLcvfrStore((s) => s.pipelineActive);
}

/**
 * Get recent run summaries for the run console.
 */
export function useLcvfrRecentRuns() {
  return useLcvfrStore((s) => s.recentRuns);
}

/**
 * Get the active encounter ID being tracked.
 */
export function useLcvfrEncounterId(): EncounterId | null {
  return useLcvfrStore((s) => s.activeEncounterId);
}
